import * as React from 'react';
import { memo, useCallback, useMemo } from 'react';

import MealComponent from '../../Meal';
import { Meal, MealCounter, MealViewMode } from '../../Meal/types';
import { SelectedMeal } from '../types';
import WebskyAnalyticsApollo from '../../DataLayerAnalytics/WebskyAnalyticsApollo';
import { WxEvents } from '../../DataLayerAnalytics/types';
import { GdsServicesFragment } from '@websky/graphql';
import { PassengerWithServicesAndID } from '../../types';
import { useGetGdsServicesCalculatedCountInfo } from '@shared/lib/gdsService/useGetGdsServicesCalculatedCountInfo';

interface MealWrapperProps {
	meal: Meal;
	fullSize?: boolean;
	segmentId: string;
	passengers: Array<PassengerWithServicesAndID & { label: string }>;
	gdsServices: GdsServicesFragment;
	selectedMeals: SelectedMeal[];
	initialSelectedMeals: SelectedMeal[];
	onMealClear: (meal: Meal, segmentId: string) => void;
	onMealConfirm?: (selected: SelectedMeal[], meal: Meal) => void;
	onMealOpen?: () => void;
	readonly?: boolean;
	isLoading?: boolean;
	mode?: MealViewMode;
}

export const MealWrapper: React.FC<MealWrapperProps> = memo(
	({
		selectedMeals,
		meal,
		segmentId,
		passengers,
		onMealConfirm,
		onMealClear,
		onMealOpen,
		readonly = false,
		mode = MealViewMode.select,
		fullSize,
		isLoading
	}) => {
		const getGdsServicesCalculatedCountInfo = useGetGdsServicesCalculatedCountInfo();

		const allowedPassengers = useMemo(() => {
			return passengers.filter(passenger => meal.allowedPassengers.includes(passenger.id));
		}, [passengers, meal.allowedPassengers]);

		const selectedMealsMap = useMemo(() => {
			const selectedMap = new Map<string, { count: number; confirmedCount: number }>();

			selectedMeals.forEach(selected => {
				const key = `${selected.mealId}/${selected.passengerId}/${selected.segmentId}`;
				const existCounts = selectedMap.get(key) ?? { count: 0, confirmedCount: 0 };

				selectedMap.set(key, {
					count: existCounts.count + selected.count,
					confirmedCount: existCounts.confirmedCount + (selected.confirmedCount ?? 0)
				});
			});

			return selectedMap;
		}, [selectedMeals]);

		const getSelectedMealCounts = useCallback(
			({ mealId, passengerId, segmentId }: { mealId: string; passengerId: string; segmentId: string }) => {
				const selectedMealKey = `${mealId}/${passengerId}/${segmentId}`;

				return selectedMealsMap.get(selectedMealKey) ?? { count: 0, confirmedCount: 0 };
			},
			[selectedMealsMap]
		);

		const onConfirmHandler = useCallback(
			(counters: MealCounter[]) => {
				onMealConfirm?.(
					counters.map(counter => ({
						count: counter.count + counter.confirmedCount,
						passengerId: counter.passenger.id,
						segmentId,
						mealId: meal.id
					})),
					meal
				);
			},
			[segmentId, meal, onMealConfirm]
		);

		const onCloseHandler = useCallback(
			(meal: Meal) => {
				onMealClear(meal, segmentId);
			},
			[segmentId, onMealClear]
		);

		const onPopupOpened = () => {
			onMealOpen?.();
			WebskyAnalyticsApollo.dispatchWxViewEvent(WxEvents.MealItem, { servicesIds: [meal.id], segmentId });
		};

		return (
			<MealComponent
				key={`${meal.id}_${segmentId}`}
				meal={meal}
				fullSize={fullSize}
				onConfirm={onConfirmHandler}
				onClose={onCloseHandler}
				readonly={readonly || !meal.canBeAdded}
				onPopupOpened={onPopupOpened}
				counters={allowedPassengers.map<MealCounter>(passenger => {
					const { count, confirmedCount } = getSelectedMealCounts({
						mealId: meal.id,
						passengerId: passenger.id,
						segmentId
					});

					const deltaCount = mode === MealViewMode.confirmed ? confirmedCount : count - confirmedCount;

					const diffCount = getGdsServicesCalculatedCountInfo(passenger.id, segmentId, {
						rfisc: meal.rfics,
						type: meal.type
					})?.diffCount;

					const maxAvailable = diffCount !== undefined ? count + diffCount : 3;

					return {
						passenger: { id: passenger.id, name: passenger.label },
						count: deltaCount,
						confirmedCount: confirmedCount,
						minAvailable: 0,
						maxAvailable
					};
				})}
				isLoading={isLoading}
				mode={mode}
			/>
		);
	}
);

MealWrapper.displayName = 'MealWrapper';
